import React, { createContext, useContext, useState, useEffect } from 'react';

const PermissionContext = createContext(null);

export const PermissionProvider = ({ user, children }) => {
    // Current active project context
    const [currentProject, setCurrentProject] = useState(null);

    // Reset loop protection / sync
    useEffect(() => {
        if (!user) setCurrentProject(null);
    }, [user]);

    /**
     * Check if user has permission.
     * @param {string} scope - 'global' or 'project'
     * @param {string} key - 'journal.create', 'team.manage', etc.
     * @returns {boolean}
     */
    const can = (scope, key) => {
        if (!user) return false;
        if (user.role === 'admin') return true; // Global Admin Override

        if (scope === 'global') {
            // Future: check user.global_permissions
            // For now, if not admin, return false for strictly global admin tasks
            return false;
        }

        if (scope === 'project') {
            if (!currentProject) return false;

            // Check if we have permissions object
            const perms = currentProject.permissions;
            if (!perms) return false;

            const [category, action] = key.split('.'); // e.g. "journal.view_log"
            const p = perms[category];
            if (!p) return false;

            // Handle new Journal Permissions
            if (category === 'journal') {
                const mode = p.mode || 'draft'; // Align with backend default
                const isFull = mode === 'full';
                // Evaluate conditional overrides

                if (action === 'view_log') return isFull;
                if (action === 'manage') return isFull && p.full_level === 'manage';
                if (action === 'approve') return isFull && p.full_level === 'manage';

                if (action === 'create') return p.create !== false; // Everyone can create (it goes to drafts) unless explicitly revoked

                if (action === 'edit_others' || action === 'delete_others') {
                    if (isFull) return p.full_level === 'manage';
                    return p.draft_action === 'all';
                }
                if (action === 'edit_own' || action === 'delete_own') {
                    if (isFull) return true; // Everyone in full mode can edit own
                    return p.draft_action !== 'none';
                }

                if (action === 'view_others') {
                    if (isFull) return true;
                    return p.draft_visibility === 'all';
                }
            }

            // Fallback for sub-categories
            return p?.[action] === true;
        }

        return false;
    };

    return (
        <PermissionContext.Provider value={{ can, setCurrentProject, currentProject, user }}>
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermission = () => {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermission must be used within a PermissionProvider');
    }
    return context;
};
